import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { CustomButton } from '../../components/common/CustomButton'
import { usePermission } from '../../hooks/usePermission'
import {
  hostedEvents,
  money,
  toCents,
  type GenerateResult,
  type RebateConfig,
  type VendorRoster,
  type VendorRow,
} from '../../services/hostedEvents'
import { Checks, RebateDetailModal } from './RebateDetailModal'
import { dateTime, downloadCsv, errorMessage } from './helpers'
import {
  Badge,
  Empty,
  ErrorBanner,
  Modal,
  Notice,
  Spinner,
  StatusBadge,
  inputClass,
  labelClass,
  rowClass,
  tableWrap,
  theadClass,
} from './ui'

type Filter = 'all' | 'eligible' | 'ineligible' | 'unopened' | 'rebated'

const matches = (vendor: VendorRow, filter: Filter) =>
  filter === 'all' ||
  (filter === 'eligible' && vendor.eligibility?.eligible) ||
  (filter === 'ineligible' && vendor.eligibility && !vendor.eligibility.eligible) ||
  (filter === 'unopened' && vendor.eligibility?.eligible && !vendor.rebate) ||
  (filter === 'rebated' && vendor.rebate)

export function VendorsPanel({
  marketId,
  config,
  onChanged,
}: {
  marketId: string
  config: RebateConfig
  onChanged: () => void
}) {
  const canReview = usePermission('event_rebates', 'review')
  const [roster, setRoster] = useState<VendorRoster | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<Filter>('all')
  const [expanded, setExpanded] = useState<string | null>(null)
  const [openRebateId, setOpenRebateId] = useState<string | null>(null)
  const [confirmGenerate, setConfirmGenerate] = useState(false)
  const [refreshPending, setRefreshPending] = useState(false)
  const [generated, setGenerated] = useState<GenerateResult | null>(null)
  const [target, setTarget] = useState<VendorRow | null>(null)
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setRoster(await hostedEvents.vendors(marketId))
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setLoading(false)
    }
  }, [marketId])

  useEffect(() => {
    load()
  }, [load])

  const changed = () => {
    load()
    onChanged()
  }

  const vendors = useMemo(
    () => (roster?.vendors || []).filter((vendor) => matches(vendor, filter)),
    [roster, filter]
  )
  const counts = useMemo(() => {
    const all = roster?.vendors || []
    return {
      all: all.length,
      eligible: all.filter((vendor) => matches(vendor, 'eligible')).length,
      ineligible: all.filter((vendor) => matches(vendor, 'ineligible')).length,
      unopened: all.filter((vendor) => matches(vendor, 'unopened')).length,
      rebated: all.filter((vendor) => matches(vendor, 'rebated')).length,
    }
  }, [roster])

  const program = roster?.program || null
  const active = program?.status === 'active'

  const generate = async () => {
    setBusy(true)
    setError(null)
    try {
      setGenerated(await hostedEvents.generate(marketId, refreshPending))
      setConfirmGenerate(false)
      changed()
    } catch (caught) {
      setError(errorMessage(caught))
      setConfirmGenerate(false)
    } finally {
      setBusy(false)
    }
  }

  const beginOpen = (vendor: VendorRow) => {
    setTarget(vendor)
    setAmount(
      vendor.eligibility?.eligible ? (vendor.eligibility.amount_cents / 100).toFixed(2) : ''
    )
    setReason('')
  }

  const amountCents = toCents(amount)
  const isException =
    !!target && (!target.eligibility?.eligible || amountCents !== target.eligibility.amount_cents)
  const openInvalid =
    amountCents === null ||
    amountCents <= 0 ||
    amountCents > config.max_rebate_cents ||
    (isException && !reason.trim())

  const openOne = async () => {
    if (!target || openInvalid) return
    setBusy(true)
    setError(null)
    try {
      await hostedEvents.open(
        marketId,
        target.application_id,
        isException ? amountCents : null,
        reason.trim() || null
      )
      setTarget(null)
      changed()
    } catch (caught) {
      setError(errorMessage(caught))
      setTarget(null)
    } finally {
      setBusy(false)
    }
  }

  const exportCsv = () =>
    downloadCsv(`vendors-${marketId.slice(0, 8)}.csv`, [
      [
        'vendor',
        'handle',
        'category',
        'spot',
        'payment',
        'booth_fee_usd',
        'refunded_usd',
        'checked_in',
        'sales',
        'sales_usd',
        'eligible',
        'rebate_usd',
        'rebate_status',
        'failed_rules',
      ],
      ...(roster?.vendors || []).map((vendor) => [
        vendor.shop_name,
        vendor.shop_handle,
        vendor.category,
        vendor.spot_label,
        vendor.payment_method,
        (vendor.booth_price_cents / 100).toFixed(2),
        (vendor.refunded_cents / 100).toFixed(2),
        vendor.checked_in_at ? 'yes' : vendor.no_show_at ? 'no-show' : 'no',
        vendor.sales_count,
        (vendor.sales_cents / 100).toFixed(2),
        vendor.eligibility ? (vendor.eligibility.eligible ? 'yes' : 'no') : '',
        vendor.rebate
          ? (vendor.rebate.amount_cents / 100).toFixed(2)
          : vendor.eligibility
            ? (vendor.eligibility.amount_cents / 100).toFixed(2)
            : '',
        vendor.rebate?.status || '',
        vendor.eligibility?.checks
          .filter((check) => !check.passed)
          .map((check) => check.label)
          .join('; ') || '',
      ]),
    ])

  if (loading && !roster) return <Spinner />

  return (
    <div>
      {error && <ErrorBanner message={error} onRetry={load} />}

      {!program && (
        <div className="mb-4">
          <Notice>
            This event has no rebate program yet. Set one up on the Rebate rules tab to see who
            qualifies.
          </Notice>
        </div>
      )}
      {program && !active && (
        <div className="mb-4">
          <Notice tone="amber">
            The program is {program.status}. Eligibility below is live, but rebates can only be
            opened while it is active.
          </Notice>
        </div>
      )}
      {generated && (
        <div className="mb-4">
          <Notice tone="green">
            Opened {generated.created} new rebate(s)
            {generated.refreshed > 0 && `, refreshed ${generated.refreshed}`}
            {generated.rejected > 0 &&
              `, rejected ${generated.rejected} that no longer qualify`}. {generated.existing}{' '}
            already had one and {generated.ineligible} vendor(s) do not qualify.
            <button type="button" className="ml-2 underline" onClick={() => setGenerated(null)}>
              Dismiss
            </button>
          </Notice>
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="Vendor filter">
          {(
            [
              ['all', 'All paid vendors'],
              ['eligible', 'Qualify'],
              ['unopened', 'Qualify, no rebate yet'],
              ['ineligible', 'Do not qualify'],
              ['rebated', 'Have a rebate'],
            ] as [Filter, string][]
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={filter === key}
              onClick={() => setFilter(key)}
              className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                filter === key
                  ? 'bg-[#7f4f9a] text-white'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-[#2d2236]'
              }`}
            >
              {label} <span className="opacity-80">{counts[key]}</span>
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <CustomButton
            size="sm"
            variant="secondary"
            onClick={exportCsv}
            disabled={!roster?.vendors.length}
          >
            Export CSV
          </CustomButton>
          {canReview && program && (
            <CustomButton
              size="sm"
              disabled={!active || (counts.unopened === 0 && counts.rebated === 0)}
              onClick={() => setConfirmGenerate(true)}
            >
              Open rebates for everyone who qualifies
            </CustomButton>
          )}
        </div>
      </div>

      {vendors.length === 0 ? (
        <Empty>
          {roster?.vendors.length
            ? 'No vendors match this filter.'
            : 'No vendor has a paid booth at this event yet.'}
        </Empty>
      ) : (
        <div className={tableWrap}>
          <table className="w-full text-sm">
            <thead className={theadClass}>
              <tr>
                <th className="px-4 py-3">Vendor</th>
                <th className="px-4 py-3 text-right">Booth fee</th>
                <th className="px-4 py-3">Check-in</th>
                <th className="px-4 py-3 text-right">In-person sales</th>
                <th className="px-4 py-3">Qualifies</th>
                <th className="px-4 py-3 text-right">Rebate</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {vendors.map((vendor) => {
                const open = expanded === vendor.application_id
                return (
                  <Fragment key={vendor.application_id}>
                    <tr className={rowClass}>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          className="text-left"
                          aria-expanded={open}
                          onClick={() => setExpanded(open ? null : vendor.application_id)}
                        >
                          <span className="font-medium text-gray-900 underline-offset-2 hover:underline dark:text-white">
                            {vendor.shop_name}
                          </span>
                          <span className="block text-xs text-gray-500 dark:text-gray-400">
                            {[
                              vendor.category,
                              vendor.tier_name,
                              vendor.spot_label && `Spot ${vendor.spot_label}`,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                        {money(vendor.booth_price_cents)}
                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                          {vendor.payment_method === 'offline' ? 'offline' : 'card'}
                          {vendor.refunded_cents > 0 &&
                            ` · ${money(vendor.refunded_cents)} refunded`}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {vendor.checked_in_at ? (
                          <Badge tone="green">Checked in</Badge>
                        ) : vendor.no_show_at ? (
                          <Badge tone="red">No-show</Badge>
                        ) : (
                          <Badge tone="gray">Not recorded</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                        {vendor.sales_count}
                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                          {money(vendor.sales_cents)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {!vendor.eligibility ? (
                          <span className="text-xs text-gray-400">No program</span>
                        ) : vendor.eligibility.eligible ? (
                          <Badge tone="green">Yes · {money(vendor.eligibility.amount_cents)}</Badge>
                        ) : (
                          <Badge tone="gray">
                            No · {vendor.eligibility.checks.filter((check) => !check.passed).length}{' '}
                            rule(s)
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {vendor.rebate ? (
                          <button
                            type="button"
                            className="inline-flex items-center gap-2"
                            onClick={() => setOpenRebateId(vendor.rebate!.id)}
                          >
                            <span className="font-medium text-gray-900 dark:text-white">
                              {money(vendor.rebate.amount_cents)}
                            </span>
                            <StatusBadge status={vendor.rebate.status} />
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">None</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canReview && program && !vendor.rebate && (
                          <CustomButton
                            size="sm"
                            variant="secondary"
                            disabled={!active}
                            onClick={() => beginOpen(vendor)}
                          >
                            {vendor.eligibility?.eligible ? 'Open rebate' : 'Exception'}
                          </CustomButton>
                        )}
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-[#fdfbff] dark:bg-[#17111d]">
                        <td colSpan={7} className="px-6 py-4">
                          <div className="grid gap-4 md:grid-cols-2">
                            <div>
                              <div className="mb-2 text-xs uppercase tracking-wider text-gray-500">
                                Rules
                              </div>
                              {vendor.eligibility ? (
                                <Checks eligibility={vendor.eligibility} />
                              ) : (
                                <span className="text-sm text-gray-500">
                                  No program to evaluate against.
                                </span>
                              )}
                            </div>
                            <dl className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                              <div>Paid: {dateTime(vendor.paid_at)}</div>
                              <div>Charged in total: {money(vendor.total_paid_cents)}</div>
                              <div>Checked in: {dateTime(vendor.checked_in_at)}</div>
                              <div>First sale in window: {dateTime(vendor.first_sale_at)}</div>
                              {vendor.shop_handle && <div>Shop: @{vendor.shop_handle}</div>}
                              {vendor.overlapping_events > 0 && (
                                <div className="text-amber-700 dark:text-amber-300">
                                  Also a paid vendor at {vendor.overlapping_events} overlapping
                                  event(s). Sales are not tied to an event, so the same sales count
                                  for each.
                                </div>
                              )}
                            </dl>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {confirmGenerate && (
        <Modal
          title="Open rebates for everyone who qualifies"
          onClose={() => setConfirmGenerate(false)}
        >
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Creates a pending rebate for each of the {counts.unopened} qualifying vendor(s) who do
            not have one. Nothing is approved or paid. Running it again is safe.
          </p>
          <label className="mt-4 flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              className="mt-1"
              checked={refreshPending}
              onChange={(event) => setRefreshPending(event.target.checked)}
            />
            <span>
              Also recalculate rebates that are still pending
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Use after changing the rules or when check-ins and sales have moved. Pending rebates
                whose vendor no longer qualifies are rejected. Exceptions are left alone.
              </span>
            </span>
          </label>
          <div className="mt-4 flex justify-end gap-2">
            <CustomButton
              variant="secondary"
              onClick={() => setConfirmGenerate(false)}
              disabled={busy}
            >
              Cancel
            </CustomButton>
            <CustomButton onClick={generate} loading={busy}>
              Open rebates
            </CustomButton>
          </div>
        </Modal>
      )}

      {target && (
        <Modal title={`Open rebate for ${target.shop_name}`} onClose={() => setTarget(null)}>
          {target.eligibility && <Checks eligibility={target.eligibility} />}
          {!target.eligibility?.eligible && (
            <div className="mt-3">
              <Notice tone="amber">
                This vendor does not qualify under the rules. Opening a rebate anyway is recorded as
                an exception and needs an amount and a reason.
              </Notice>
            </div>
          )}
          <label className={`${labelClass} mt-4`} htmlFor="open-amount">
            Amount ($)
          </label>
          <input
            id="open-amount"
            className={inputClass}
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
          <label className={`${labelClass} mt-3`} htmlFor="open-reason">
            Reason{isException ? '' : ' (optional)'}
          </label>
          <textarea
            id="open-reason"
            className={inputClass}
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="mt-4 flex justify-end gap-2">
            <CustomButton variant="secondary" onClick={() => setTarget(null)} disabled={busy}>
              Cancel
            </CustomButton>
            <CustomButton onClick={openOne} loading={busy} disabled={openInvalid}>
              {isException ? 'Open as exception' : 'Open rebate'}
            </CustomButton>
          </div>
        </Modal>
      )}

      {openRebateId && (
        <RebateDetailModal
          rebateId={openRebateId}
          config={config}
          onClose={() => setOpenRebateId(null)}
          onChanged={changed}
        />
      )}
    </div>
  )
}
